import chroma from 'chroma-js';

export const DEFAULT_COLOR_OFFSET = 0.5;

export const VARIABLE_COLORS = {
	primaryColor: '--primary-color',
	primaryColorLighten: '--primary-color-lighten',
	primaryColorDarken: '--primary-color-darken',
	primaryColorFont: '--primary-color-font',

	secondaryColor: '--secondary-color',
	secondaryColorLighten: '--secondary-color-lighten',
	secondaryColorDarken: '--secondary-color-darken',
	secondaryColorFont: '--secondary-color-font',

	infoColor: '--info-color',
	infoColorLighten: '--info-color-lighten',
	infoColorDarken: '--info-color-darken',
	infoColorFont: '--info-color-font',

	successColor: '--success-color',
	successColorLighten: '--success-color-lighten',
	successColorDarken: '--success-color-darken',
	successColorFont: '--success-color-font',

	warningColor: '--warning-color',
	warningColorLighten: '--warning-color-lighten',
	warningColorDarken: '--warning-color-darken',
	warningColorFont: '--warning-color-font',

	helpColor: '--help-color',
	helpColorLighten: '--help-color-lighten',
	helpColorDarken: '--help-color-darken',
	helpColorFont: '--help-color-font',

	dangerColor: '--danger-color',
	dangerColorLighten: '--danger-color-lighten',
	dangerColorDarken: '--danger-color-darken',
	dangerColorFont: '--danger-color-font',

	backgroundColor: '--background-color',
	surfaceGround: '--surface-ground',
	surfaceOverlay: '--surface-overlay',
	surfaceSection: '--surface-section',
	surfaceCard: '--surface-card',
	surfaceBorder: '--surface-border',
	surfaceBorderCard: '--surface-border-card',
	surfaceHover: '--surface-hover',
	surfaceHoverRoot: '--surface-hover-root',
	surfaceHeader: '--surface-header',

	textColor: '--text-color',
	textColorSecondary: '--text-color-secondary',
	textColorRoot: '--text-color-root',
	textColorSecondaryRoot: '--text-color-secondary-root',
	textColorCard: '--text-color-card',
	textColorSecondaryCard: '--text-color-secondary-card',

	borderRadius: '--border-radius',
	footerBackgroundColor: '--footer-background-color',
	footerForegroundColor: '--footer-foreground-color',
} as const;

export type VariableColorKeys = keyof typeof VARIABLE_COLORS;
export type VariableColorValues = typeof VARIABLE_COLORS[VariableColorKeys];
export type TTheme = { [key in VariableColorKeys]: string };

const getVariableValue = (variable: VariableColorValues) => {
	const value = getComputedStyle(document.documentElement).getPropertyValue(
		variable
	);

	if (value.match(/rem/g)) return +value.replace('rem', '');

	return value.trim();
};

let theme = {} as TTheme;
const getVariablesColors = () => {
	if (Object.keys(theme).length === 0)
		for (const [key, value] of Object.entries(VARIABLE_COLORS)) {
			const cssValue = getVariableValue(value);

			if (typeof cssValue !== 'number' && chroma.valid(cssValue)) {
				const [r, g, b] = chroma(getVariableValue(value)).rgb();
				theme = { ...theme, [key]: { r, g, b } };
			} else theme = { ...theme, [key]: cssValue };
		}

	return theme;
};

const setVariableValue = (
	variable: VariableColorKeys,
	value: string | number
): void => {
	const isNumber = typeof value === 'number';

	if (typeof value === 'object') value = chroma(value).hex();
	if (isNumber) value = `${value}rem`;

	value = value as string;

	for (const [variableKey, variableValue] of Object.entries(VARIABLE_COLORS)) {
		if (
			[
				variable,
				`${variable}Lighten`,
				`${variable}Darken`,
				`${variable}Font`,
			].includes(variableKey)
		) {
			if (variableKey === variable) {
				setVariableDOM(variableValue, value);
				if (isNumber) return;
			}

			const lightenColor =
				chroma(value).luminance() > 0.3
					? chroma(value).darken(DEFAULT_COLOR_OFFSET).hex()
					: chroma(value).brighten(DEFAULT_COLOR_OFFSET).hex();

			const darkenColor =
				chroma(value).luminance() > 0.3
					? chroma(value).darken(DEFAULT_COLOR_OFFSET).hex()
					: chroma(value).brighten(DEFAULT_COLOR_OFFSET).hex();

			const colorAlpha = chroma(
				chroma(value).luminance() > 0.3 ? 'black' : 'white'
			)
				.alpha(0.75)
				.hex();

			const fontColor = chroma(
				chroma(value).luminance() > 0.3 ? 'black' : 'white'
			).hex();

			if (variableKey === `${variable}Lighten`)
				setVariableDOM(variableValue, lightenColor);

			if (variableKey === `${variable}Darken`)
				setVariableDOM(variableValue, darkenColor);

			if (variableKey === `${variable}Font`)
				setVariableDOM(variableValue, fontColor);

			if (variableKey === 'backgroundColor') {
				setVariableDOM(VARIABLE_COLORS.surfaceHoverRoot, darkenColor);
				setVariableDOM(VARIABLE_COLORS.textColorRoot, fontColor);
				setVariableDOM(VARIABLE_COLORS.textColorSecondaryRoot, colorAlpha);
			}

			if (variableKey === 'surfaceSection') {
				setVariableDOM(VARIABLE_COLORS.surfaceOverlay, value);
				setVariableDOM(VARIABLE_COLORS.surfaceBorder, darkenColor);
				setVariableDOM(VARIABLE_COLORS.surfaceHover, darkenColor);
				setVariableDOM(VARIABLE_COLORS.surfaceGround, darkenColor);
				setVariableDOM(VARIABLE_COLORS.textColor, fontColor);
				setVariableDOM(VARIABLE_COLORS.textColorSecondary, colorAlpha);
			}

			if (variableKey === 'surfaceCard') {
				setVariableDOM(VARIABLE_COLORS.surfaceBorderCard, fontColor);
				setVariableDOM(VARIABLE_COLORS.textColorCard, fontColor);
				setVariableDOM(VARIABLE_COLORS.textColorSecondaryCard, colorAlpha);
			}
		}
	}
};

const setVariableDOM = (variable: VariableColorValues, value: string): void =>
	document.documentElement.style.setProperty(variable, value);

const setThemeConfig = (variables: {
	[key in VariableColorKeys]: string | null;
}) => {
	for (const [key, value] of Object.entries(variables)) {
		if (value !== null) {
			setVariableValue(key as VariableColorKeys, value);
		}
	}
};

const saveTheme = (variables: {
	[key in VariableColorKeys]: string;
}) => {
	let newTheme = {} as any;
	for (const [key, value] of Object.entries(variables))
		if (typeof value !== 'number' && key !== 'name') {
			newTheme[key] = chroma(value).hex();
		} else newTheme[key] = value;

	return newTheme;
};

const toRGBTheme = (variables: {
	[key in VariableColorKeys]: string | null;
}) => {
	let newTheme = {} as any;
	for (const [key, value] of Object.entries(variables)) {
		if (value !== null && typeof value !== 'number' && key !== 'name') {
			const [r, g, b] = chroma(value).rgb();
			newTheme[key] = { r, g, b };
		} else {
			newTheme[key] = value;
		}
	}
	return newTheme;
};

const setColorSchemeValue = (variables: {
	[key in VariableColorKeys]: string;
}) => {
	let colorScheme = {} as any;
	for (const [key, value] of Object.entries(variables)) {
		let colorSch = {} as any;
		colorSch = setColorScheme(key as VariableColorKeys, value);

		if (colorSch !== undefined) {
			colorScheme = { ...colorScheme, ...colorSch };
		}
	}
	return colorScheme;
};

const setColorScheme = (
	variable: VariableColorKeys,
	value: string | number
): void => {
	const isNumber = typeof value === 'number';

	if (typeof value === 'object') value = chroma(value).hex();
	if (isNumber) value = `${value}rem`;

	value = value as string;

	let colorScheme = {} as any;

	for (const [variableKey] of Object.entries(VARIABLE_COLORS)) {
		if (
			[
				variable,
				`${variable}Lighten`,
				`${variable}Darken`,
				`${variable}Font`,
			].includes(variableKey)
		) {
			if (variableKey === variable) {
				colorScheme[variableKey] = value;
				if (isNumber) return;
			}

			const lightenColor =
				chroma(value).luminance() > 0.3
					? chroma(value).darken(DEFAULT_COLOR_OFFSET).hex()
					: chroma(value).brighten(DEFAULT_COLOR_OFFSET).hex();

			const darkenColor =
				chroma(value).luminance() > 0.3
					? chroma(value).darken(DEFAULT_COLOR_OFFSET).hex()
					: chroma(value).brighten(DEFAULT_COLOR_OFFSET).hex();

			const colorAlpha = chroma(
				chroma(value).luminance() > 0.3 ? 'black' : 'white'
			)
				.alpha(0.75)
				.hex();

			const fontColor = chroma(
				chroma(value).luminance() > 0.3 ? 'black' : 'white'
			).hex();

			if (variableKey === `${variable}Lighten`) {
				colorScheme[variableKey] = lightenColor;
			}

			if (variableKey === `${variable}Darken`) {
				colorScheme[variableKey] = darkenColor;
			}

			if (variableKey === `${variable}Font`) {
				colorScheme[variableKey] = fontColor;
			}

			if (variableKey === 'backgroundColor') {
				colorScheme['surfaceHoverRoot'] = darkenColor;
				colorScheme['textColorRoot'] = fontColor;
				colorScheme['textColorSecondaryRoot'] = colorAlpha;
			}

			if (variableKey === 'surfaceSection') {
				colorScheme['surfaceOverlay'] = value;
				colorScheme['surfaceBorder'] = darkenColor;
				colorScheme['surfaceHover'] = darkenColor;
				colorScheme['surfaceGround'] = darkenColor;
				colorScheme['textColor'] = fontColor;
				colorScheme['textColorSecondary'] = colorAlpha;
			}

			if (variableKey === 'surfaceCard') {
				colorScheme['surfaceBorderCard'] = darkenColor;
				colorScheme['textColorCard'] = fontColor;
				colorScheme['textColorSecondaryCard'] = colorAlpha;
			}
		}
	}
	return colorScheme;
};

export {
	getVariableValue,
	getVariablesColors,
	setVariableValue,
	setThemeConfig,
	setColorSchemeValue,
	theme,
	saveTheme,
	toRGBTheme,
};
